import { type FormikHelpers } from 'formik'
import * as React from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import { type CommonError } from 'src/api/base'
import Statements from 'src/assets/icons/customIcons/page-icons/Statements'
import { errorMessage, formatFileSize } from 'src/helpers/fns'
import { useApi } from 'src/helpers/hooks'
import { sanitizeHtml } from 'src/helpers/sanitizeHtml'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Section } from 'src/tailwind/components/Section'
import FileIcon from 'src/views/components/FileIcon'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import { FormSubmit } from 'src/views/components/forms/formik/FormSubmit'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import FormikTinyMce from 'src/views/components/forms/FormikTinyMce'
import Loader from 'src/views/components/Loader'
import { MediaFilesDownload } from 'src/views/components/MediaFilesDownload'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import { mutate as mutateCache } from 'swr'
import { formatStatementDate, type StatementLinkState } from './statementsQuery'

interface ReviewValues {
  readonly status: string
  readonly comment: string
}

export default function AdministratorStatementPage(): JSX.Element {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('statement:statement')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()
  const navigate = useNavigate()
  const headers = useAuthenticatedHeaders()
  const { id, semId } = useParams() as { readonly id: string; readonly semId: string }
  const listPath = `/${locale}/administrator/administration/statements`
  // Back to the list with the filters it was opened from; a reloaded page has no state and gets the plain list
  const linkState = useLocation().state as StatementLinkState | null
  const filteredListPath = `${listPath}${linkState?.listSearch ?? ''}`

  const {
    data: statement,
    error,
    mutate,
  } = useApi({
    endpoint: Api.getAdministratorStatement,
    params: React.useMemo(() => ({ headers, args: { id, semId } }), [headers, id, semId]),
    suspense: false,
  })

  // e.g. a statement id opened with another semester's id
  React.useEffect(() => {
    if (error != null) {
      toast.error(errorMessage(error as unknown as CommonError))
      navigate(filteredListPath, { replace: true })
    }
  }, [error, navigate, filteredListPath])

  const [isSaved, setIsSaved] = React.useState(false)

  const handleSaved = React.useCallback(
    (updated: Api.AdministratorStatement) => {
      void mutate(updated, { revalidate: false })
      // Drop cached list pages so the list does not flash the old status and colour before refetching
      void mutateCache((key) => Array.isArray(key) && key[0] === Api.getAdministratorStatements, undefined, {
        revalidate: false,
      })
      setIsSaved(true)
    },
    [mutate]
  )

  // After the form has re-rendered as clean, so its unsaved-changes prompt does not block the navigation
  React.useEffect(() => {
    if (isSaved) {
      navigate(filteredListPath)
    }
  }, [isSaved, navigate, filteredListPath])

  const breadcrumbsItems = [
    { page: `${t('system:administration_sidebar')} - ${t('statement:statements')}`, path: filteredListPath },
    { page: statement?.number, path: `${listPath}/${id}/${semId}` },
  ]

  return (
    <Section
      title={statement != null ? `${t('statement:statement')} #${statement.number}` : t('statement:statement')}
      breadcrubms={breadcrumbsItems}
      icon={<Statements />}
    >
      {statement == null ? (
        <Loader className="m-auto my-10 flex" />
      ) : (
        <div className="flex flex-col gap-6">
          <StatementDetails statement={statement} />
          <StatementBody statement={statement} />
          <StatementFiles files={statement.files ?? []} />
          <ReviewForm statement={statement} onSaved={handleSaved} />
        </div>
      )}
    </Section>
  )
}

function StatementDetails({ statement }: { readonly statement: Api.AdministratorStatement }): JSX.Element {
  const t = useTranslatable()
  const { student, commenter } = statement

  const rows: ReadonlyArray<readonly [string, string]> = [
    [t('statement:template'), statement.title],
    [t('student:student'), student.fullName],
    [t('person:personal_number'), student.persNum],
    [t('person:email'), student.mail ?? ''],
    [t('common:created_at'), formatStatementDate(statement.createdAt)],
    [t('common:status'), statement.statusText ?? '—'],
    ...(commenter != null
      ? ([
          [t('statement:reviewed_by'), commenter.fullName],
          [t('statement:review_date'), formatStatementDate(statement.commentDate)],
        ] as const)
      : []),
  ]

  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 rounded-card bg-card p-4 text-primaryTextColor md:grid-cols-[max-content_1fr]">
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt className="text-captionColor">{label}</dt>
          <dd className="mb-0 break-words">{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  )
}

/** The body is the rendered paper statement with its own inline colours, so it keeps a light background in dark mode. */
function StatementBody({ statement }: { readonly statement: Api.AdministratorStatement }): JSX.Element {
  const body = React.useMemo(() => sanitizeHtml(statement.body), [statement.body])

  return (
    <div
      className="overflow-x-auto rounded-card border border-borderGrey bg-white p-6 text-black"
      data-testid="statement-body"
      dangerouslySetInnerHTML={{ __html: body }}
    />
  )
}

function StatementFiles({ files }: { readonly files: readonly Api.StatementFile[] }): JSX.Element | null {
  const t = useTranslatable()

  if (files.length === 0) return null

  return (
    <div className="text-primaryTextColor" data-testid="statement-files">
      <p className="mb-2 font-bold">{t('file:attached_files')}</p>
      <ul className="flex flex-col gap-1 pl-0">
        {files.map((file) => (
          <li key={file.id} className="flex items-center gap-2">
            <FileIcon type={file.mediaFile.extension} />
            <MediaFilesDownload mediaFileId={file.mediaFile.id} target="_blank">
              {file.mediaFile.originalName}
            </MediaFilesDownload>
            <span className="text-sm text-captionColor">({formatFileSize(file.mediaFile.size)})</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

interface ReviewFormProps {
  readonly statement: Api.AdministratorStatement
  readonly onSaved: (updated: Api.AdministratorStatement) => void
}

/** Everyone who can open the page may review it, viewer roles included, as in old Argus. */
function ReviewForm({ statement, onSaved }: ReviewFormProps): JSX.Element {
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()

  const { data: statuses, isLoading: statusesLoading } = useApi({
    endpoint: Api.getAdministratorListsStatementStatuses,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const statusOptions = React.useMemo(
    () => (statuses ?? []).map((item) => ({ value: String(item.id), label: item.name })),
    [statuses]
  )

  // An out-of-range status (statusText null) preselects nothing, so a valid one has to be chosen
  const initialValues = React.useMemo(
    (): ReviewValues => ({
      status: statement.statusText != null ? String(statement.status) : '',
      comment: statement.comment ?? '',
    }),
    [statement]
  )

  const handleSubmit = React.useCallback(
    async (values: ReviewValues, formikHelpers: FormikHelpers<ReviewValues>) => {
      // The comment is replaced on every save; leaving it out would erase it
      const updated = await Api.putAdministratorStatement({
        headers,
        args: { id: statement.id, semId: String(statement.semId) },
        body: { status: parseInt(values.status, 10), comment: values.comment },
      })
      toast.success(t('statement:saved'))
      formikHelpers.resetForm({ values })
      onSaved(updated)
    },
    [headers, statement.id, statement.semId, onSaved, t]
  )

  return (
    <Form initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize isConfirmable>
      <div className="flex flex-col gap-2">
        <SelectInput
          label={t('common:status')}
          name="status"
          options={statusOptions}
          isLoading={statusesLoading}
          required
        />
        <FormikTinyMce name="comment" label={t('discussion:comment')} />
        <FormError />
        <FormSubmit
          label={t('common:save')}
          classNames={{ root: 'w-full flex justify-end border-t border-borderGrey py-4', button: 'h-[48px]' }}
        />
      </div>
    </Form>
  )
}
